import { useRef } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { useDialogContext } from '../../../../../../contexts/DialogContext';
import { window } from '../../../../../../globals';
import { useOnUnmount } from '../../../../../../hooks';
import projectGrouping from '../../../../../../redux/project_grouping';
import { BUTTON_SIZE, icons, LoaderButton, RouterLink } from '../../../../../generic';
import FallbackImage from '../../../../../generic/FallbackImage';
import { usePortalUrl } from '../../../../../shared/publicPortal/hooks';

import styles from './PublishButton.module.less';

type Props = {
  isDisabled?: boolean;
  reportType: 'SBOM' | 'Attribution';
  onPublishReport: () => Promise<void>;
  isLoading: boolean;
  isPublished: boolean;
  replaceReportText?: string;
  publishedReportText?: string;
  publishReportText?: string;
};

const PublishButton: VoidFunctionComponent<Props> = ({
  isDisabled,
  reportType,
  onPublishReport,
  isLoading,
  isPublished,
  replaceReportText,
  publishedReportText,
  publishReportText,
}) => {
  const { onConfirm, onAlert } = useDialogContext();
  const { url: portalUrl } = usePortalUrl();
  const removeDialogCallbackRef = useRef<(() => void) | null>(null);
  const isReportPublished = useSelector(projectGrouping.selectors.getIsReportPublished);
  const replaceReportButtonText = replaceReportText || `Replace ${reportType}`;
  const publishReportButtonText = publishReportText || `Publish ${reportType}`;
  const publishedReportButtonText = publishedReportText || `Published ${reportType}`;
  const publishOrReplaceReportButtonText = isReportPublished ? replaceReportButtonText : publishReportButtonText;

  // It is important to remove the dialog when the component is unmounted.
  // Otherwise, the dialog will be left in the context and will be shown in the
  // settings page if the user clicks the link in the modal.
  useOnUnmount(() => {
    if (removeDialogCallbackRef.current) {
      removeDialogCallbackRef.current();
    }
  });

  const publishButtonAction = () => {
    if (!window.portalUrl) {
      onAlert({
        title: 'Feature not available',
        body: (
          <div className={styles.dialog}>
            <FallbackImage src="404-cat" alt="Unconfigured feature" hasPadding>
              <div>This feature is not configured</div>
              <p className={styles.pleaseEnable}>Please ask your FOSSA administrator to enable it.</p>
            </FallbackImage>
          </div>
        ),
      });
      return;
    }

    removeDialogCallbackRef.current = onConfirm({
      title: isReportPublished ? `Replace ${reportType}` : `Publish ${reportType}`,
      body: (
        <div className={styles.dialog}>
          {portalUrl && (
            <>
              <p>
                This {reportType} will be published to:{' '}
                <RouterLink to={portalUrl} openInNewTab>
                  {portalUrl}
                </RouterLink>
              </p>
              <p>
                This is a public URL and will be visible to anyone you share it with. You can modify the portal in your{' '}
                <RouterLink to="/account/settings/organization/portal">Org Settings</RouterLink>.
              </p>
            </>
          )}
          {!portalUrl && (
            <p>
              The report will not be accessible until you set up your organization portal. You can do this in{' '}
              <RouterLink to="/account/settings/organization/portal">Org Settings</RouterLink>.
            </p>
          )}
        </div>
      ),
      onConfirm: onPublishReport,
    });
  };

  return (
    <LoaderButton
      isDisabled={isPublished || isDisabled}
      isLoading={isLoading}
      size={BUTTON_SIZE.LARGE}
      icon={icons.bookRegular}
      onClick={publishButtonAction}
    >
      {isPublished ? publishedReportButtonText : publishOrReplaceReportButtonText}
    </LoaderButton>
  );
};

export default PublishButton;
